import React, { useState, useMemo, useEffect } from "react";
import { getResultLabel, getResultColor, fmtDur, SITUATIONS_MAP } from "./helpers";
import {
  getReviewState,
  isRelevantEvent,
  REVIEW_LABELS,
  REVIEW_COLORS,
  confirmEvent,
  validateEvent,
} from "../utils/review";

/**
 * ReviewInbox — Bandeja de revisión de eventos (Fase 2).
 * Cola de eventos cerrados a la izquierda, detalle compacto a la derecha,
 * acciones grandes y avance automático al siguiente pendiente.
 *
 * Props:
 *   alarms: array de alarmas cargadas (con docID, zoneName, etc.)
 *   roles: { canReview, canValidate } (de getReviewRoles)
 *   user: usuario autenticado
 *   onEdit(alarm): abre el modal "Editar evento" (por encima de la bandeja)
 *   onOpenDetail(alarm): abre el EventDetail completo
 *   onGuide(): abre la guía de situaciones
 *   onClose(): cierra la bandeja
 */
export default function ReviewInbox({ alarms, roles, user, facilityId, onEdit, onOpenDetail, onGuide, onReport, onClose }) {
  const [tab, setTab] = useState("pending"); // pending | relevant | reviewed
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const closed = useMemo(
    () => (alarms || []).filter((a) => a.resolvedAt && a.docID)
      .sort((x, y) => (y.createdAt?.seconds || 0) - (x.createdAt?.seconds || 0)),
    [alarms]
  );

  const queue = useMemo(() => {
    if (tab === "pending") return closed.filter((a) => getReviewState(a) === "pending");
    if (tab === "relevant") return closed.filter((a) => getReviewState(a) === "pending" && isRelevantEvent(a));
    return closed.filter((a) => getReviewState(a) !== "pending");
  }, [closed, tab]);

  const pendingCount = useMemo(() => closed.filter((a) => getReviewState(a) === "pending").length, [closed]);
  // Casos validados del periodo → alimentan el reporte entregable
  const validatedList = useMemo(
    () => closed.filter((a) => getReviewState(a) === "validated")
      .sort((x, y) => (x.createdAt?.seconds || 0) - (y.createdAt?.seconds || 0)),
    [closed]
  );

  // Selección: mantenerla válida; si el seleccionado sale de la cola (p.ej. se
  // confirmó), avanzar automáticamente al siguiente.
  const selected = queue.find((a) => a.docID === selectedId) || null;
  useEffect(() => {
    if (!selected && queue.length > 0) setSelectedId(queue[0].docID);
    if (queue.length === 0) setSelectedId(null);
  }, [queue, selected]);

  const act = async (fn, a) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn(a, user, facilityId);
      // el onSnapshot del padre actualiza `alarms`; el useEffect avanza solo
    } catch (e) {
      console.error("ReviewInbox action error:", e);
      setError("No se pudo guardar. Reintenta.");
    } finally {
      setBusy(false);
    }
  };

  const state = selected ? getReviewState(selected) : null;
  const relevant = selected ? isRelevantEvent(selected) : false;
  const startMs = selected?.createdAt?.seconds ? selected.createdAt.seconds * 1000 : null;

  const abtn = (bg, fg, border) => ({
    padding: "8px 13px", borderRadius: 7, border: `1px solid ${border || bg}`,
    background: bg, color: fg, fontSize: 11.5, fontWeight: 700,
    cursor: busy ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 5,
    opacity: busy ? 0.6 : 1,
  });
  const F = ({ k, v, warn }) => (
    <div style={{ padding: "3px 0", display: "grid", gridTemplateColumns: "110px 1fr", gap: "2px 10px", fontSize: 11 }}>
      <span style={{ color: "var(--muted)", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: ".03em" }}>{k}</span>
      <span style={{ color: warn ? "#fbbf24" : "var(--text)", lineHeight: 1.4 }}>{v}</span>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9990,
        background: "rgba(2,6,17,0.8)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 14,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 1050, height: "min(640px, 92vh)",
          background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12,
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--bright)" }}>📋 Bandeja de revisión</div>
            <div style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 1 }}>
              {pendingCount === 0 ? "Sin pendientes en el periodo cargado 🎉" : `${pendingCount} evento${pendingCount === 1 ? "" : "s"} por revisar en el periodo cargado`}
            </div>
          </div>
          <span style={{ flex: 1 }} />
          {validatedList.length > 0 && onReport && (
            <button
              type="button"
              onClick={() => onReport(validatedList)}
              title={`Generar el reporte con los ${validatedList.length} casos validados del periodo`}
              style={abtn("#22c55e", "#08130b")}
            >📄 Reporte ({validatedList.length})</button>
          )}
          <button type="button" onClick={onGuide} style={abtn("transparent", "var(--muted)", "var(--border)")}>? Guía</button>
          <button type="button" onClick={onClose} style={abtn("var(--surface)", "var(--subtle)", "var(--border)")}>✕ Cerrar</button>
        </div>

        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* ── Cola ── */}
          <div style={{ width: 300, flexShrink: 0, borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{ display: "flex", gap: 4, padding: 8, borderBottom: "1px solid var(--border)" }}>
              {[
                ["pending", `Pendientes · ${closed.filter((a) => getReviewState(a) === "pending").length}`],
                ["relevant", `⭐ Relevantes · ${closed.filter((a) => getReviewState(a) === "pending" && isRelevantEvent(a)).length}`],
                ["reviewed", "Revisados"],
              ].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTab(k)}
                  style={{
                    padding: "4px 8px", borderRadius: 999, fontSize: 8.5, fontWeight: 700, cursor: "pointer",
                    border: `1px solid ${tab === k ? "#94a3b8" : "var(--border)"}`,
                    background: tab === k ? "#94a3b81a" : "transparent",
                    color: tab === k ? "var(--text)" : "var(--muted)",
                    whiteSpace: "nowrap",
                  }}
                >{l}</button>
              ))}
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {queue.length === 0 ? (
                <div style={{ padding: 16, fontSize: 10.5, color: "var(--muted)", textAlign: "center" }}>
                  {tab === "reviewed" ? "Aún no hay eventos revisados." : "Nada pendiente aquí. ✓"}
                </div>
              ) : queue.map((a) => {
                const st = getReviewState(a);
                const active = a.docID === selectedId;
                const ms = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : null;
                return (
                  <div
                    key={a.docID}
                    onClick={() => setSelectedId(a.docID)}
                    style={{
                      display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 10px",
                      borderBottom: "1px solid #1c2740", cursor: "pointer",
                      background: active ? "#3b82f612" : "transparent",
                      borderLeft: active ? "2px solid #3b82f6" : "2px solid transparent",
                    }}
                  >
                    <span style={{
                      width: 14, height: 14, borderRadius: "50%", flexShrink: 0, marginTop: 1,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontSize: 8.5, fontWeight: 800,
                      ...(st === "pending"
                        ? { border: "1.5px dashed var(--dim)" }
                        : { background: `${REVIEW_COLORS[st]}22`, color: REVIEW_COLORS[st], border: `1px solid ${REVIEW_COLORS[st]}55` }),
                    }}>{st === "pending" ? "" : st === "edited" ? "✎" : "✓"}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--bright)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.zoneName || "—"}</div>
                      <div style={{ fontSize: 8.5, color: "var(--muted)", fontFamily: "var(--mono)" }}>
                        {ms ? new Date(ms).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}
                        {" · "}{getResultLabel(a)}
                        {isRelevantEvent(a) ? " · ⭐" : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Detalle compacto ── */}
          <div style={{ flex: 1, minWidth: 0, padding: 14, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {!selected ? (
              <div style={{ margin: "auto", textAlign: "center", color: "var(--muted)", fontSize: 12 }}>
                {tab === "reviewed" ? "Selecciona un evento de la lista." : "🎉 No queda nada por revisar en esta cola."}
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "var(--bright)" }}>{selected.zoneName || "—"}</div>
                    <div style={{ fontSize: 9, color: "var(--muted)", fontFamily: "var(--mono)", marginTop: 1 }}>
                      {startMs ? new Date(startMs).toLocaleString("es-CL") : "—"} · ID {selected.docID?.slice(0, 8)}… · duración {selected.duration != null ? fmtDur(selected.duration) : "—"}
                    </div>
                  </div>
                  <span style={{
                    padding: "3px 9px", borderRadius: 5, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap",
                    background: `${getResultColor(selected)}1f`, border: `1px solid ${getResultColor(selected)}3d`, color: getResultColor(selected),
                  }}>{getResultLabel(selected)}</span>
                </div>

                <div style={{ height: 10 }} />
                <F k="Situación" v={selected.situation ? `${selected.situation} · ${SITUATIONS_MAP[selected.situation] || ""}` : "—"} />
                <F k="Operador CCTV" v={selected.closedBy || selected.dispatchedBy || selected.guardName || "—"} />
                <F k="Guardias" v={(selected.guards?.length ? selected.guards.join(", ") : selected.guardName) || "—"} />
                <F k="Patente" v={selected.vehicle?.plate || selected.plate || "—"} />
                {selected.vehicle?.desc && <F k="Vehículo" v={selected.vehicle.desc} />}
                <F k="Sospechoso" v={selected.suspectDesc || (selected.suspect ? "Sí" : "—")} />
                <F k="Comentarios" v={selected.comments || selected.notes || "—"} />
                {selected.lateReason && <F k="Gestión tardía" v={selected.lateReason} warn />}

                {/* Estado actual */}
                {state !== "pending" && (
                  <div style={{
                    marginTop: 10, padding: "5px 9px", borderRadius: 6, fontSize: 9.5,
                    background: `${REVIEW_COLORS[state]}12`, border: `1px solid ${REVIEW_COLORS[state]}35`,
                    color: REVIEW_COLORS[state],
                  }}>
                    {state === "validated" ? "🛡 " : ""}{REVIEW_LABELS[state]}
                    {state === "validated" && selected.validatedBy ? ` · por ${selected.validatedBy}` : selected.reviewedBy ? ` · por ${selected.reviewedBy}` : ""}
                  </div>
                )}

                <span style={{ flex: 1 }} />

                {/* Acciones */}
                <div style={{ borderTop: "1px solid var(--border)", marginTop: 12, paddingTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {state !== "validated" && (
                    <button type="button" disabled={busy} onClick={() => act(confirmEvent, selected)} style={abtn("#3b82f6", "#071022")}>
                      ✓ Confirmar
                    </button>
                  )}
                  <button type="button" disabled={busy} onClick={() => onEdit(selected)} style={abtn("#f59e0b22", "#fbbf24", "#f59e0b55")}>
                    ✎ Corregir…
                  </button>
                  {roles?.canValidate && relevant && state !== "validated" && (
                    <button type="button" disabled={busy} onClick={() => act(validateEvent, selected)} style={abtn("#22c55e", "#08130b")}>
                      🛡 Validar caso
                    </button>
                  )}
                  <span style={{ flex: 1 }} />
                  <button type="button" onClick={() => onOpenDetail(selected)} style={abtn("transparent", "var(--muted)", "var(--border)")}>
                    Ver detalle completo
                  </button>
                </div>
                <div style={{ fontSize: 8.5, color: "var(--dim)", marginTop: 6 }}>
                  Al confirmar o validar, la bandeja avanza automáticamente al siguiente pendiente.
                  {roles?.canValidate && !relevant && state !== "validated" ? " Validar solo está disponible en eventos relevantes." : ""}
                </div>
                {error && <div style={{ fontSize: 10, color: "#ef4444", marginTop: 6 }}>{error}</div>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
